import snGame from "../assets/images/snooker-game.jpg";
import { apiService } from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { React, useEffect, useState } from "react";

const GameCard = ({ game, creationType, onDelete, navigate }) => {
    return (
        <div className="game" key={game.id}>
            <img src={snGame} alt="Snooker Game" className="game-img" />
            <div className="layer"></div>
            <span className="status" data-status={game.status}>{game.status}</span>
            <span className="table">{game.name}</span>
            <span className="players">{game.first_player_name} vs {game.second_player_name}</span>
            <div className="game-actions">
                {creationType === 'created' && (
                    <span className="delete" onClick={() => onDelete(game)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-trash w-4 h-4 text-white">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M4 7l16 0" />
                            <path d="M10 11l0 6" />
                            <path d="M14 11l0 6" />
                            <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                            <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                        </svg>
                    </span>
                )}
                <span className="stats" onClick={() => navigate(`/game/stats/${game.link}`)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-device-desktop-analytics">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M3 4m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1z" />
                        <path d="M7 20h10" />
                        <path d="M9 16v4" />
                        <path d="M15 16v4" />
                        <path d="M9 12v-4" />
                        <path d="M12 12v-1" />
                        <path d="M15 12v-2" />
                        <path d="M12 12v-1" />
                    </svg>
                </span>
            </div>
        </div>
    );
};

const GameTab = ({ games, activePane, creationType, setActivePane, onDelete, navigate }) => {
    const gameTypes = ['snooker', 'pool', 'us-pool', 'chinese-pool'];
    const filteredGames = games.filter(g => g.deleted_at === null && g.creation_type === creationType);

    return (
        <div className="section w-100">
            {creationType === 'created' && <p className="section-title">Finished Matches</p>}
            <div className="card">
                <div className="card-header">
                    <ul className="nav nav-tabs card-header-tabs nav-fill" data-bs-toggle="tabs" role="tablist">
                        {gameTypes.map(type => (
                            filteredGames.filter(g => g.type === type).length > 0 && (
                                <li className="nav-item cursor-pointer" role="presentation" key={type}>
                                    <span onClick={() => setActivePane(type)} className={`nav-link ${activePane === type ? 'active' : ''}`}>
                                        {type === 'chinese-pool' ? 'Hey ball' : type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                                    </span>
                                </li>
                            )
                        ))}
                    </ul>
                </div>
                <div className="card-body">
                    <div className="tab-content">
                        {gameTypes.map(type => (
                            filteredGames.filter(g => g.type === type).length > 0 && (
                                <div className={`tab-pane ${activePane === type ? 'active show' : ''}`} key={type}>
                                    <div className="games">
                                        {filteredGames.filter(g => g.type === type).map(game => (
                                            <GameCard
                                                key={game.id}
                                                game={game}
                                                creationType={creationType}
                                                onDelete={onDelete}
                                                navigate={navigate}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const FinishedGames = () => {
    const navigate = useNavigate();
    const [games, setGames] = useState([]);
    const [definedGamesActivePane, setDefinedGamesActivePane] = useState('snooker');
    const [loading, setLoading] = useState(true);
    const { user, isAuthenticated } = useAuth();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteLabel, setDeleteLabel] = useState('');

    const getClubFinishedGames = async (id) => {
        const data = await apiService.post('/club/finished-games', { id_user: id });
        setGames(data.games);
    };

    const deleteGame = async (id) => {
        await apiService.post('/club/game/delete', {
            id: id,
            id_user: user.id
        });
        setGames(games.filter(g => g.id !== id));
    };

    const requestDelete = (game) => {
        setDeleteId(game.id);
        setDeleteLabel(`${game.name} — ${game.first_player_name} vs ${game.second_player_name}`);
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (isDeleting || deleteId == null) return;
        setIsDeleting(true);
        try {
            await deleteGame(deleteId);
            setConfirmOpen(false);
            setDeleteId(null);
            setDeleteLabel('');
        } catch (e) {
            console.error(e);
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        if (user) {
            setLoading(true);
            getClubFinishedGames(user.id).finally(() => {
                setLoading(false);
            });
        }
    }, [user]);

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="page-body">
                    <div className="container-xl w-100">
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">
                                Finished Matches
                            </div>
                            {isAuthenticated() ?
                                <h2 className="page-title">
                                    {(user) ? user.name : ''}
                                </h2>
                                : null
                            }
                        </div>
                    </div>
                </div>
            </div>
            <div className="page-body">
                <div className="container-xl">
                    <div className="row row-deck row-cards">
                        <div className="col-12">
                            <GameTab
                                games={games}
                                activePane={definedGamesActivePane}
                                creationType="created"
                                setActivePane={setDefinedGamesActivePane}
                                onDelete={requestDelete}
                                navigate={navigate}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={`modal modal-blur fade ${confirmOpen ? 'show' : ''}`} id="modal-danger" tabIndex="-1" role="dialog" aria-hidden="true" style={confirmOpen ? { display: 'block' } : { display: 'none' }}>
                <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-status bg-danger"></div>
                        <div className="modal-body text-center py-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon mb-2 text-danger icon-lg" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M12 9v2m0 4v.01" />
                                <path d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75" />
                            </svg>
                            <h3>Are you sure?</h3>
                            <div className="text-muted">Do you really want to remove the following game: <strong>{deleteLabel}</strong></div>
                        </div>
                        <div className="modal-footer">
                            <div className="w-100">
                                <div className="row">
                                    <div className="col">
                                        <button className="btn w-100" data-bs-dismiss="modal" onClick={() => setConfirmOpen(false)}>
                                            Cancel
                                        </button>
                                    </div>
                                    <div className="col">
                                        <button className="btn btn-danger w-100" data-bs-dismiss="modal" onClick={confirmDelete}>
                                            Delete game
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinishedGames;